// Pure helpers for the dashboard's live-update path: applying an SSE patch,
// merging a scoped fetch, and deciding which sessions the next poll should ask
// about. Kept out of AnalyticsViewContent so they can be tested without
// mounting a ~1000-line component.
//
// All three preserve reference identity when nothing changed. That is not a
// micro-optimisation: the poll effect depends on `data`, so returning a fresh
// array for a no-op would clear and re-arm its timer on every stray event.

import { Key } from "../constants/enums";
import type { AnalyticsData, BiteData } from "../types/data";

/** The id the grid keys a tile on. Must match the mapper and the backend's
 *  (session_id, slide_idx) pair. */
export const buildBiteId = (sessionId: string, slideIdx: number): string =>
  `${sessionId}:${slideIdx}`;

/**
 * Mark one bite as awaiting analysis, in place.
 *
 * Input:  rows as displayed; the session and slide the SSE event named
 * Output: rows with that one bite's isProcessing set — or `rows` unchanged if
 *         the row is absent (a session outside this teacher's current view) or
 *         the bite is already processing (a duplicate or replayed event)
 */
export function patchBiteProcessing(
  rows: AnalyticsData[],
  sessionId: string,
  slideIdx: number,
): AnalyticsData[] {
  const biteId = buildBiteId(sessionId, slideIdx);
  let changed = false;

  const next = rows.map((row) => {
    if (row.sessionId !== sessionId) return row;

    const bites = row[Key.BITES] as BiteData[];
    const idx = bites.findIndex((b) => b.biteId === biteId);
    if (idx === -1 || bites[idx].isProcessing) return row;

    const nextBites = bites.slice();
    nextBites[idx] = { ...nextBites[idx], isProcessing: true };
    changed = true;
    return { ...row, [Key.BITES]: nextBites };
  });

  return changed ? next : rows;
}

/**
 * Fold a scoped fetch's rows onto the rows already on screen.
 *
 * Input:  the current rows; the rows a scoped fetch returned
 * Output: current rows with each fetched row substituted for its match
 *
 * Position is preserved — the grid's own sort runs downstream, but reordering
 * here would make rows jump under the cursor on every poll.
 *
 * Fetched rows with no match are dropped rather than appended. A scoped fetch
 * only ever names sessions we already hold, so an unmatched row means the
 * server returned something outside the current view; appending it would put a
 * row on screen that the active filters never admitted.
 */
export function mergeSessionRows(
  current: AnalyticsData[],
  fetched: AnalyticsData[],
): AnalyticsData[] {
  if (fetched.length === 0) return current;

  const bySessionId = new Map<string, AnalyticsData>();
  for (const row of fetched) {
    if (row.sessionId) bySessionId.set(row.sessionId, row);
  }
  if (bySessionId.size === 0) return current;

  let changed = false;
  const next = current.map((row) => {
    const replacement = row.sessionId
      ? bySessionId.get(row.sessionId)
      : undefined;
    if (!replacement || replacement === row) return row;
    changed = true;
    return replacement;
  });

  return changed ? next : current;
}

/**
 * The sessions the next poll should ask about.
 *
 * Input:  rows as displayed
 * Output: unique session ids holding at least one bite awaiting analysis
 *
 * Deliberately derived from `data` rather than tracked in its own state: a
 * separate set would be a second source of truth that could disagree with what
 * is on screen, and every path that changes a tile already flows through here.
 */
export function processingSessionIds(rows: AnalyticsData[]): string[] {
  const ids = new Set<string>();
  for (const row of rows) {
    if (!row.sessionId) continue;
    const bites = row[Key.BITES] as BiteData[];
    if (bites.some((b) => b.isProcessing)) ids.add(row.sessionId);
  }
  return Array.from(ids);
}

/**
 * Bites that just finished analysis: awaiting analysis in `prevRows`, no
 * longer in `nextRows`. This is the only moment the attempts endpoint's
 * answer for that bite actually changed — the SSE event that started the
 * pulse fires before the Lambda runs, so it names nothing new yet.
 *
 * Input:  rows before this poll's merge; rows after it
 * Output: (sessionId, slideIdx) pairs whose attempt-list cache is now stale
 */
export function resolvedProcessingBites(
  prevRows: AnalyticsData[],
  nextRows: AnalyticsData[],
): Array<{ sessionId: string; slideIdx: number }> {
  const wasProcessing = new Set<string>();
  for (const row of prevRows) {
    const bites = row[Key.BITES] as BiteData[];
    for (const bite of bites) {
      if (bite.isProcessing) wasProcessing.add(bite.biteId);
    }
  }
  if (wasProcessing.size === 0) return [];

  const resolved: Array<{ sessionId: string; slideIdx: number }> = [];
  for (const row of nextRows) {
    const bites = row[Key.BITES] as BiteData[];
    for (const bite of bites) {
      if (!wasProcessing.has(bite.biteId) || bite.isProcessing) continue;
      const [sessionId, slideIdxStr] = bite.biteId.split(":");
      resolved.push({ sessionId, slideIdx: Number(slideIdxStr) });
    }
  }
  return resolved;
}
